import * as React from "react";
import * as S from "@styled";
import * as _ from "lodash";
import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm'
import CustomModal from "shared/components/CustomModal";
import { PageLoadSpinner } from "shared/components/PageLoadSpinner";
import { TableWithPagination } from "shared/components/Table";
import { DeploymentInformationProps } from "../../../processList/components/DeploymentInformation";
import { DeploymentInformationIcon } from "../../../processList/components/DeploymentInformationIcon";
import { AgentGroup, defaultAgentGroupName, } from "../../../shared/model/AgentGroup";
import { ChangeEvent } from "../../../shared/model/Event";
import { Utilities } from "../../../shared/utilities";
import { handleToggleDropdown } from "shared/styles/helpers";
import { CellContext, ColumnDef, createColumnHelper } from "@tanstack/react-table";
import DropdownButton from "shared/components/DropdownButton";
import Time from "shared/Time";

type TableProcessRow = {
	version: number;
	combinedVersion: string;
	modified: string;
	modifier: string;
	description: string;
	deployedTo: AgentGroup[];
	actions?: string;
	id: number;
}

const ProcessChangeLogModal: React.FC<ProcessChangeLogModalProps> = (props) => {

	const [state, setState] = React.useState<ProcessChangeLogModalState>({ processChangeLogList: [], isLoading: true, editingProcessId: null, newDescription: "" });

	React.useEffect(() => {
		async function fetch() {
			await getChangeLogData();
		}
		fetch();
	}, []);

	const getChangeLogData = async () => {
		try {
			const data = await Utilities.getFetch<ProcessChangeLogModel[]>(`api/process/changelog/${props.processGuid}`);
			setState((curState) => ({
				...curState,
				processChangeLogList: data,
				editingProcessId: null,
				newDescription: "",
				isLoading: false
			}));
		} catch (err) {
			Utilities.toastError(err);
		}
	};

	const processVersionDescription = (cellProps: CellContext<TableProcessRow, string>) => {
		return (
			<ReactMarkdown
				className="react-markdown-description"
				remarkPlugins={[remarkGfm]}
				components={{
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					a: ({ node, ...props }) => <a target="_blank" rel="noopener noreferrer" {...props} />
				}}
			>
				{cellProps.row.original.description}
			</ReactMarkdown>
		)
	};

	const getDeployedTo = (agentGroup: AgentGroup) => {
		const deploymentInformationProps: DeploymentInformationProps = {
			processGuid: props.processGuid,
			agentGroup: agentGroup.name,
		};

		// Conceptually there are no deployments to the default agent group, so we hide the icon.
		// To line things up nicely in the ui it is still created though, just hidden.
		const showDeploymentInformationIcon = (agentGroup.name !== defaultAgentGroupName);

		const overlayTrigger = <DeploymentInformationIcon
			deploymentInformationProps={deploymentInformationProps}
			show={showDeploymentInformationIcon}
			popoverId={agentGroup.name}
			title={FRENDS.Localizations.DeploymentInformation}
		/>;

		return (
			<S.FlexContainer key={agentGroup.name} asRow gap={4}>
				{overlayTrigger} {agentGroup.displayName}
			</S.FlexContainer>
		);
	};

	const processVersionDeployedTo = (cellProps: CellContext<TableProcessRow, AgentGroup[]>) => {
		const agentGroups: AgentGroup[] = cellProps.row.original.deployedTo;
		if (_.isEmpty(agentGroups)) {
			return "";
		}
		const title = agentGroups.map((ag: AgentGroup) => ag.displayName).join(", ");
		const content = agentGroups.map(getDeployedTo);
		return <span title={title}>{content}</span>;
	};


	const setEditDescription = (processId: number) => {
		setState((curState) => ({
			...curState,
			editingProcessId: processId
		}));
	};

	const cancelEditDescription = () => {
		setState((curState) => ({
			...curState,
			editingProcessId: null,
			newDescription: ""
		}));
	};

	const saveNewDescription = async () => {
		try {
			await Utilities.postJsonAndParseResponse(`api/process/appendChangeDescription/${state.editingProcessId}`, {
				NewChangeDescription: state.newDescription
			});
			Utilities.toastSuccess("Success");
			await getChangeLogData();
		} catch (error) {
			Utilities.toastError("Could not modify description." + error);
			console.log(error);
		}
	};

	const updateDescription = (event: ChangeEvent<HTMLTextAreaElement>) => {
		setState((curState) => ({
			...curState,
			newDescription: event.target.value
		}));
	};

	const processVersionActions = (cellProps: CellContext<TableProcessRow, string | undefined>) => {
		const processVersionActionsDropdownRefName = `process-versions-actions-${cellProps.row.original.version}`;
		return (
			<DropdownButton
				id={processVersionActionsDropdownRefName}
				buttonComponent={(
					<S.Button type="button" variant={S.ButtonType.text}
						className="dropbtn"
						id={processVersionActionsDropdownRefName + "-dropdown"}
						title="More actions"
						onClick={() => handleToggleDropdown(processVersionActionsDropdownRefName, 'process-changelog-modal')}
					>
						<i className="ci-more_horizontal" />
					</S.Button>
				)}
			>
				<a href={`${props.isSubProcess ? "SubProcess" : "Process"}/Edit/${cellProps.row.original.id}`}>Edit</a>
				<a href={`Process/Diff/${props.processGuid}?leftVersion=${cellProps.row.original.version}`}>Diff</a>
				<button onClick={() => setEditDescription(cellProps.row.original.id)}>Append description</button>
				<a href={`api/Process/${props.processGuid}/Version/${cellProps.row.original.version}/Package`} download>Download package</a>
			</DropdownButton>
		)
	};

	let versionRowData: TableProcessRow[] = state.processChangeLogList.map((process) => ({
		version: process.version,
		combinedVersion: process.combinedVersion,
		modified: Time.toDetailedLocalTime(process.modified),
		modifier: process.modifier,
		description: process.changeDescription,
		deployedTo: process.deployedAgentGroups,
		id: process.processId
	})).sort((a, b) => b.version - a.version); // Sort by descending order ACC-7449

	const columnHelper = createColumnHelper<TableProcessRow>();

	const tableColumns = React.useMemo<Array<ColumnDef<TableProcessRow>>>(() => [
		columnHelper.accessor("combinedVersion", {
			header: FRENDS.Localizations.Version
		}),
		columnHelper.accessor("modified", {
			header: FRENDS.Localizations.Modified
		}),
		columnHelper.accessor("modifier", {
			header: FRENDS.Localizations.Modifier
		}),
		columnHelper.accessor("description", {
			header: FRENDS.Localizations.Description,
			cell: cell => processVersionDescription(cell)
		}),
		columnHelper.accessor("deployedTo", {
			header: FRENDS.Localizations.DeployedTo,
			cell: cell => processVersionDeployedTo(cell)
		}),
		columnHelper.accessor("actions", {
			header: "",
			cell: cell => processVersionActions(cell)
		})
	], []);

	if (state.editingProcessId) {
		versionRowData = versionRowData.filter((x) => x.id === state.editingProcessId)
	}

	if (state.isLoading) {
		return <PageLoadSpinner />;
	}

	if (!state.processChangeLogList || state.processChangeLogList.length < 1) {
		return null;
	}

	return (
		<CustomModal
			label="process-changelog-modal"
			onClose={props.onHide}
			shouldCloseOnOverlayClick={true}
			show={true}
			size="large"
			modalTitle={<h2>{props.processName} {FRENDS.Localizations.ChangeLog}</h2>}
		>
			<S.FormGroup>
				<TableWithPagination
					gridTemplateColumns="auto 1fr 1fr 2fr 1fr auto"
					shouldWrapData={true}
					columns={tableColumns}
					data={versionRowData}
				/>
			</S.FormGroup>
			{state.editingProcessId && (
				<S.FormGroup>
					<S.TextArea
						className="form-control"
						rows={3}
						value={state.newDescription}
						onChange={updateDescription}
					>
					</S.TextArea>
				</S.FormGroup>
			)}
			{state.editingProcessId ?
				<S.ButtonGroup gap={12}>
					<S.Button variant={S.ButtonType.bordered} onClick={cancelEditDescription}>{FRENDS.Localizations.Cancel}</S.Button>
					<S.Button onClick={saveNewDescription}>{FRENDS.Localizations.SaveChanges}</S.Button>
				</S.ButtonGroup>
				:
				<S.ButtonGroup>
					<S.Button onClick={props.onHide}>{FRENDS.Localizations.Close}</S.Button>
				</S.ButtonGroup>
			}

		</CustomModal>
	);
};

interface ProcessChangeLogModalProps {
	onHide: () => void;
	processId: number | undefined;
	isSubProcess: boolean | undefined;
	processName: string | undefined;
	processGuid: string | undefined;
}

export interface ProcessChangeLogModalState {
	processChangeLogList: ProcessChangeLogModel[];
	isLoading: boolean;
	error?: any;
	editingProcessId: number | null;
	newDescription?: string;
}

export interface ProcessChangeLogModel {
	processId: number;
	version: number;
	combinedVersion: string;
	modified: string;
	modifier: string;
	changeDescription: string;
	deployedAgentGroups: AgentGroup[];
}

export default ProcessChangeLogModal;
